import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import baseUrl from "../api/utils";

// ═══════════════════════════════════════════════
// PUBLIC INVOICE VIEW — no login required.
// Anyone with the link (yourapp.com/public-invoice/<shareToken>)
// can open this page and see only that one invoice.
// Looked up by an unguessable share token, never by the raw DB id,
// so invoices can't be enumerated by walking sequential ids.
// ═══════════════════════════════════════════════
const PublicInvoiceView = () => {
  const { token } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [downloading, setDownloading] = useState(false);
  const invoiceRef = useRef(null); // captures just the white card, not the whole page

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        // NOTE: no Authorization header — this hits the public backend route
        const res = await axios.get(`${baseUrl}/public/invoice/token/${token}`);
        setInvoice(res.data);
      } catch (err) {
        console.error("Failed to fetch public invoice", err);
        setError("Invoice not found or no longer available.");
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [token]);

  // ═══════════════════════════════════════════════
  // DOWNLOAD PDF — same html2canvas + jsPDF approach used in BillPreview.jsx,
  // captures the invoice card only (not the page background) and saves it
  // as a single-page PDF sized to the card's own dimensions.
  // ═══════════════════════════════════════════════
  const handleDownloadPdf = async () => {
    if (!invoiceRef.current) return;
    try {
      setDownloading(true);
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2,          // sharper output
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");

      const pdfWidth = canvas.width;
      const pdfHeight = canvas.height;
      const pdf = new jsPDF({
        orientation: pdfWidth > pdfHeight ? "landscape" : "portrait",
        unit: "px",
        format: [pdfWidth, pdfHeight],
      });
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

      const fileName = `Invoice_${invoice.invoicePrefix || ""}${invoice.invoiceNumber || ""}.pdf`;
      pdf.save(fileName);
    } catch (err) {
      console.error("Failed to generate PDF", err);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <div className="spinner-border text-danger"></div>
        <p style={{ marginTop: 12, color: "#888" }}>Loading invoice...</p>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div style={{ textAlign: "center", padding: "60px 20px" }}>
        <i className="fa-solid fa-circle-exclamation" style={{ fontSize: 40, color: "#e74c3c" }}></i>
        <p style={{ marginTop: 12, color: "#555" }}>{error || "Invoice not found."}</p>
      </div>
    );
  }

  const isPaid = !invoice.balanceAmount || Number(invoice.balanceAmount) <= 0;

  return (
    <div style={{ maxWidth: 700, margin: "0 auto", padding: "24px 16px" }}>
      {/* ── Download button — sits outside the ref'd card so it isn't captured in the PDF ── */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button
          onClick={handleDownloadPdf}
          disabled={downloading}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "8px 16px", borderRadius: 8, border: "none",
            background: downloading ? "#aaa" : "#e74c3c", color: "#fff",
            fontSize: 13, fontWeight: 600, cursor: downloading ? "default" : "pointer",
          }}
        >
          <i className={`fa-solid ${downloading ? "fa-spinner fa-spin" : "fa-download"}`}></i>
          {downloading ? "Preparing..." : "Download PDF"}
        </button>
      </div>

      <div ref={invoiceRef} style={{
        background: "#fff",
        borderRadius: 10,
        border: "1px solid #eee",
        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        padding: 24,
      }}>
        {/* ── Header ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <h4 style={{ margin: 0 }}>Invoice</h4>
            <div style={{ fontSize: 13, color: "#888", marginTop: 4 }}>
              #{invoice.invoicePrefix || ""}{invoice.invoiceNumber}
            </div>
          </div>
          <span style={{
            padding: "4px 12px", borderRadius: 14, fontSize: 12, fontWeight: 600,
            background: isPaid ? "#e6f4ea" : "#fdecea",
            color:      isPaid ? "#137333" : "#d93025",
          }}>
            {isPaid ? "Paid" : "Unpaid"}
          </span>
        </div>

        {/* ── Bill To / Date ── */}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20, fontSize: 14 }}>
          <div>
            <div style={{ color: "#888", fontSize: 12, marginBottom: 2 }}>BILL TO</div>
            <div style={{ fontWeight: 600 }}>{invoice.partyName}</div>
            {invoice.phoneNo && (
              <div style={{ color: "#888", fontSize: 12, marginTop: 2 }}>{invoice.phoneNo}</div>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ color: "#888", fontSize: 12, marginBottom: 2 }}>DATE</div>
            <div>{invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString("en-GB") : "—"}</div>
          </div>
        </div>

        {/* ── Items table ── */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, marginBottom: 20 }}>
          <thead>
            <tr style={{ background: "#f5f5f5" }}>
              <th style={{ textAlign: "left",  padding: "8px 10px" }}>Item</th>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>Qty</th>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>Rate</th>
              <th style={{ textAlign: "right", padding: "8px 10px" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.items || []).map((item, idx) => (
              <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: "8px 10px" }}>{item.itemName}</td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>{item.qty} {item.unit || ""}</td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>₹{Number(item.priceWithoutTax || 0).toFixed(2)}</td>
                <td style={{ padding: "8px 10px", textAlign: "right" }}>₹{Number(item.totalAmount || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ── Totals ── */}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <div style={{ width: 240 }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 14 }}>
              <span style={{ color: "#888" }}>Total</span>
              <span style={{ fontWeight: 600 }}>₹{Number(invoice.grandTotal || 0).toFixed(2)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 14 }}>
              <span style={{ color: "#888" }}>Received</span>
              <span>₹{Number(invoice.amountReceived || 0).toFixed(2)}</span>
            </div>
            <div style={{
              display: "flex", justifyContent: "space-between", padding: "8px 0", marginTop: 6,
              borderTop: "1px solid #eee", fontSize: 15, fontWeight: 700,
              color: isPaid ? "#27ae60" : "#e74c3c"
            }}>
              <span>Balance Due</span>
              <span>₹{Number(invoice.balanceAmount || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ textAlign: "center", marginTop: 16, fontSize: 12, color: "#aaa" }}>
        This is a shared invoice view. Contact the sender for any queries.
      </div>
    </div>
  );
};

export default PublicInvoiceView;